import {
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
} from 'recharts'
import { useState, useEffect, useMemo } from 'react'
import { ArrowUpRight, ArrowDownRight, Minus, User, ChevronDown, ChevronUp, Landmark, Building2, HardHat, PencilRuler, UserCog, Check, DollarSign, Calendar, Users, Target, Percent, Smile, RefreshCw, TrendingUp, Pencil } from 'lucide-react'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { collection, query, orderBy, limit, getDocs, doc, getDoc } from 'firebase/firestore'
import { db } from '../services/firebase'
import { OrgChartService } from '../services/orgChartService'
import { useAuth } from '../contexts/AuthContext'
import { DashboardCard, Top20Clients } from '../components/Dashboard'
import { useActiveWorkOverview } from '../hooks/useActiveWorkOverview'
import { useExecutiveSummaryGoals } from '../hooks/useExecutiveSummaryGoals'
import { useExecutiveSummaryProfitMargin } from '../hooks/useExecutiveSummaryProfitMargin'
import { useMarginEfficiencyMargins } from '../hooks/useMarginEfficiencyMargins'
import { computeMonthlyForecast, type ProjectMarginRow } from '../components/ProfitabilityPerformance/mockData'
import { savePwfProfitabilitySnapshotFromPwf } from '../services/pwfProfitabilitySnapshotService'
import { NPS_DATA } from '../data/npsData'
import type { ClientType } from '../data/top20ClientsData'
import { EXECUTIVE_SUMMARY, EXECUTIVE_SUMMARY_PROFIT_MARGIN_START_DATE } from '../data/executiveSummaryData'
import type { ExecutiveSummaryGoals } from '../data/executiveSummaryData'

const CLIENT_TYPE_STYLE: Record<ClientType, { bg: string; text: string; icon: React.ElementType }> = {
  Council: { bg: 'bg-slate-100', text: 'text-slate-800', icon: Landmark },
  Developer: { bg: 'bg-blue-100', text: 'text-blue-800', icon: Building2 },
  Builder: { bg: 'bg-amber-100', text: 'text-amber-800', icon: HardHat },
  Architect: { bg: 'bg-violet-100', text: 'text-violet-800', icon: PencilRuler },
  Consultant: { bg: 'bg-teal-100', text: 'text-teal-800', icon: UserCog },
}

const SATISFACTION_COLORS = {
  veryDissatisfied: '#DA4773',
  dissatisfied: '#E2A15C',
  neutral: '#C9B6DA',
  satisfied: '#96D4BF',
  verySatisfied: '#7ab8a3',
} as const

const SATISFACTION_LEGEND_ORDER: (keyof typeof SATISFACTION_COLORS)[] = [
  'veryDissatisfied',
  'dissatisfied',
  'neutral',
  'satisfied',
  'verySatisfied',
]

function ComingSoonBadge({ className = '' }: { className?: string }) {
  return (
    <span className={`inline-flex items-center rounded-md bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 ${className}`}>
      Coming soon
    </span>
  )
}

function TrendIcon({ change }: { change: number }) {
  if (change > 0) return <ArrowUpRight className="w-4 h-4 text-emerald-500" />
  if (change < 0) return <ArrowDownRight className="w-4 h-4 text-red-500" />
  return <Minus className="w-4 h-4 text-gray-400" />
}

/** Promoters (#B3DBCB), passives (#E7A96A), detractors (#c44d71) */
const NPS_DONUT_COLORS = ['#B3DBCB', '#E7A96A', '#c44d71'] as const

function MiniMonthRing({
  month,
  score,
  breakdown,
}: {
  month: string
  score: number
  breakdown: { promoters: number; passives: number; detractors: number }
}) {
  const [showTooltip, setShowTooltip] = useState(false)
  const r = 28
  const circumference = 2 * Math.PI * r
  const segmentLabels = ['Promoters', 'Passives', 'Detractors']
  const segments = [
    { pct: breakdown.promoters, color: NPS_DONUT_COLORS[0] },
    { pct: breakdown.passives, color: NPS_DONUT_COLORS[1] },
    { pct: breakdown.detractors, color: NPS_DONUT_COLORS[2] },
  ].map((s, i) => ({ ...s, label: segmentLabels[i], length: (s.pct / 100) * circumference }))
  return (
    <div className="flex flex-col items-center">
      <div
        className="relative inline-flex items-center justify-center cursor-help"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <svg width={64} height={64} className="-rotate-90">
          <circle cx={32} cy={32} r={r} fill="none" stroke="#e5e7eb" strokeWidth={6} />
          {segments.map((seg, i) => {
            const offset = segments.slice(0, i).reduce((sum, s) => sum + s.length, 0)
            return (
              <circle
                key={i}
                cx={32}
                cy={32}
                r={r}
                fill="none"
                stroke={seg.color}
                strokeWidth={6}
                strokeDasharray={`${seg.length} ${circumference - seg.length}`}
                strokeDashoffset={-offset}
                strokeLinecap="round"
              />
            )
          })}
        </svg>
        <span className="absolute text-sm font-bold text-gray-900 pointer-events-none">{score}</span>
        {showTooltip && (
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1.5 bg-gray-900 text-white text-xs rounded shadow-lg whitespace-nowrap z-10">
            {segments.map((s) => (
              <div key={s.label}>{s.label}: {s.pct}%</div>
            ))}
          </div>
        )}
      </div>
      <span className="text-xs text-gray-500 mt-1">{month}</span>
    </div>
  )
}

function NpsInfographic() {
  const { currentScore, responses, changeVsLastMonth, breakdown, monthlyTrend } = NPS_DATA
  const donutData = [
    { name: 'Promoters', value: breakdown.promoters, color: NPS_DONUT_COLORS[0] },
    { name: 'Passives', value: breakdown.passives, color: NPS_DONUT_COLORS[1] },
    { name: 'Detractors', value: breakdown.detractors, color: NPS_DONUT_COLORS[2] },
  ]
  const trendLabel =
    changeVsLastMonth > 0 ? `+${changeVsLastMonth}` : changeVsLastMonth === 0 ? '0' : String(changeVsLastMonth)

  return (
    <DashboardCard title="NPS Score" accent="rose" className="lg:col-span-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="flex flex-col items-center justify-center">
          <div className="relative w-48 h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Tooltip
                  formatter={(value, name) => [`${value ?? 0}%`, name ?? '']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                />
                <Pie
                  data={donutData}
                  dataKey="value"
                  innerRadius="65%"
                  outerRadius="100%"
                  paddingAngle={0}
                  stroke="none"
                >
                  {donutData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-3xl font-bold text-gray-900">{currentScore}%</span>
              <span className="text-xs text-gray-500 mt-0.5">NPS Score</span>
            </div>
          </div>
          <div className="mt-2 px-4 py-2 bg-gray-100 rounded-lg">
            <span className="text-sm font-medium text-gray-700">{responses} Respondents</span>
          </div>
          <div className="flex items-center gap-1.5 text-sm mt-2">
            <TrendIcon change={changeVsLastMonth} />
            <span
              className={
                changeVsLastMonth > 0 ? 'text-emerald-600' : changeVsLastMonth < 0 ? 'text-red-600' : 'text-gray-500'
              }
            >
              {trendLabel} vs last month
            </span>
          </div>
        </div>

        <div className="flex flex-col">
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="flex flex-col items-center p-3 rounded-lg border border-[#c44d71]/60" style={{ backgroundColor: 'rgba(196, 77, 113, 0.2)' }}>
              <User className="w-8 h-8" style={{ color: '#c44d71' }} aria-hidden />
              <span className="text-sm font-semibold text-gray-900 mt-1">{breakdown.detractors}%</span>
              <span className="text-xs text-gray-600">Detractors</span>
            </div>
            <div className="flex flex-col items-center p-3 rounded-lg border border-[#E7A96A]/60" style={{ backgroundColor: 'rgba(231, 169, 106, 0.3)' }}>
              <User className="w-8 h-8" style={{ color: '#E7A96A' }} aria-hidden />
              <span className="text-sm font-semibold text-gray-900 mt-1">{breakdown.passives}%</span>
              <span className="text-xs text-gray-600">Passives</span>
            </div>
            <div className="flex flex-col items-center p-3 rounded-lg border border-[#B3DBCB]/60" style={{ backgroundColor: 'rgba(179, 219, 203, 0.35)' }}>
              <User className="w-8 h-8" style={{ color: '#5BA392' }} aria-hidden />
              <span className="text-sm font-semibold text-gray-900 mt-1">{breakdown.promoters}%</span>
              <span className="text-xs text-gray-600">Promoters</span>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Monthly NPS</h4>
            <div className="grid grid-cols-4 gap-4">
              {monthlyTrend.map((point, i) => (
                <MiniMonthRing
                  key={i}
                  month={point.month}
                  score={point.nps_score}
                  breakdown={breakdown}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </DashboardCard>
  )
}

function SatisfactionChart() {
  const { satisfactionAspects } = NPS_DATA
  const chartData = satisfactionAspects.map(({ aspect, breakdown }) => ({
    aspect: aspect.length > 35 ? aspect.slice(0, 34) + '…' : aspect,
    fullAspect: aspect,
    ...breakdown,
  }))
  return (
    <DashboardCard title="Satisfaction by Values" className="lg:col-span-2" accent="rose">
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 8, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis type="number" unit="%" domain={[0, 100]} tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="aspect" width={220} tick={{ fontSize: 11 }} />
            <Tooltip
              contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
              formatter={(value: number | undefined) => [`${value ?? 0}%`, '']}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null
                const row = payload[0].payload as { fullAspect?: string }
                return (
                  <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
                    <p className="font-medium text-gray-900 mb-1">{row.fullAspect ?? ''}</p>
                    {payload.map((entry) => (
                      <p key={entry.dataKey} className="text-gray-600">
                        {entry.name}: {entry.value}%
                      </p>
                    ))}
                  </div>
                )
              }}
            />
            <Legend
              content={({ payload }) => {
                const ordered = payload
                  ? [...payload].sort(
                      (a, b) =>
                        SATISFACTION_LEGEND_ORDER.indexOf(a.dataKey as keyof typeof SATISFACTION_COLORS) -
                        SATISFACTION_LEGEND_ORDER.indexOf(b.dataKey as keyof typeof SATISFACTION_COLORS)
                    )
                  : []
                return (
                  <div className="flex flex-wrap justify-center gap-4 mt-2">
                    {ordered.map((entry) => (
                      <span key={entry.value} className="inline-flex items-center gap-1.5 text-xs">
                        <span
                          className="inline-block w-3 h-3 rounded-sm shrink-0"
                          style={{ backgroundColor: entry.color }}
                        />
                        <span>{entry.value}</span>
                      </span>
                    ))}
                  </div>
                )
              }}
            />
            <Bar
              dataKey="veryDissatisfied"
              stackId="sat"
              fill={SATISFACTION_COLORS.veryDissatisfied}
              name="Very Dissatisfied"
            />
            <Bar
              dataKey="dissatisfied"
              stackId="sat"
              fill={SATISFACTION_COLORS.dissatisfied}
              name="Dissatisfied"
            />
            <Bar
              dataKey="neutral"
              stackId="sat"
              fill={SATISFACTION_COLORS.neutral}
              name="Neutral"
            />
            <Bar
              dataKey="satisfied"
              stackId="sat"
              fill={SATISFACTION_COLORS.satisfied}
              name="Satisfied"
            />
            <Bar
              dataKey="verySatisfied"
              stackId="sat"
              fill={SATISFACTION_COLORS.verySatisfied}
              name="Very Satisfied"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </DashboardCard>
  )
}

function FeedbackTable() {
  const { feedback } = NPS_DATA
  return (
    <DashboardCard title="Recent Feedback" accent="rose">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Client
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Project
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                NPS Score
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Comment
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {feedback.map((row, idx) => (
              <tr key={idx} className={idx % 2 === 1 ? 'bg-gray-50' : undefined}>
                <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{row.client}</td>
                <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{row.project}</td>
                <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{row.score}</td>
                <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{row.category}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{row.comment}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashboardCard>
  )
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-NZ', {
    style: 'currency',
    currency: 'NZD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

function OnTrackBadge({ onTrack }: { onTrack: boolean }) {
  if (!onTrack) return null
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium mt-1.5 text-emerald-600">
      <Check className="w-3.5 h-3.5" aria-hidden />
      On track
    </span>
  )
}

function goalCompletionPct(current: number, goal: number): number {
  if (goal === 0) return 0
  return Math.round((current / goal) * 100)
}

function isBvtOrgUser(email: string | null | undefined): boolean {
  return Boolean(email?.toLowerCase().endsWith('@bvt.co.nz'))
}

function EditableGoalLine({
  displayValue,
  editValue,
  format,
  editable,
  muted,
  saving,
  onSave,
}: {
  displayValue: string
  editValue: number
  format: 'currency' | 'percent' | 'number'
  editable: boolean
  muted?: boolean
  saving?: boolean
  onSave: (value: number) => void | Promise<void>
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const opacity = muted ? 'opacity-60' : ''

  useEffect(() => {
    if (!editing) {
      setDraft(
        format === 'currency'
          ? String(Math.round(editValue))
          : format === 'percent'
            ? String(editValue)
            : String(editValue),
      )
    }
  }, [editValue, editing, format])

  const commit = async () => {
    const parsed = Number.parseFloat(draft.replace(/[^0-9.-]/g, ''))
    if (!Number.isFinite(parsed) || parsed < 0) {
      setEditing(false)
      return
    }
    if (parsed === editValue) {
      setEditing(false)
      return
    }
    try {
      await onSave(parsed)
      setEditing(false)
    } catch {
      // Keep edit open; parent shows save error.
    }
  }

  if (editing && editable) {
    return (
      <div className={`flex items-center gap-1.5 text-xs ${opacity}`}>
        <span className="text-gray-500 shrink-0">Goal:</span>
        <input
          type="text"
          inputMode="decimal"
          className="min-w-0 flex-1 rounded border border-gray-300 bg-white px-1.5 py-0.5 text-xs text-gray-900 tabular-nums focus:border-dashboard-mint focus:outline-none focus:ring-1 focus:ring-dashboard-mint"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => {
            void commit()
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              void commit()
            }
            if (event.key === 'Escape') {
              event.preventDefault()
              setEditing(false)
            }
          }}
          autoFocus
          disabled={saving}
          aria-label="Edit goal"
        />
        {format === 'percent' ? <span className="text-gray-500">%</span> : null}
      </div>
    )
  }

  if (!editable) {
    return (
      <p className={`text-xs text-gray-500 ${opacity}`}>
        Goal: {displayValue}
      </p>
    )
  }

  return (
    <p className={`group text-xs text-gray-500 ${opacity}`}>
      Goal:{' '}
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="inline-flex items-center gap-1 rounded px-0.5 -mx-0.5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-1 focus:ring-dashboard-mint"
        aria-label={`Edit goal (${displayValue})`}
      >
        <span>{displayValue}</span>
        <Pencil className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-60" aria-hidden />
      </button>
    </p>
  )
}

/** Mini x/y chart: time on x, revenue on y. Goal line (start→end) and current line (start→today). Red if behind, green if on/above goal. */
function RevenueMiniChart({
  goal,
  current,
  periodStart,
  periodEnd,
  onTrack,
}: {
  goal: number
  current: number
  periodStart: Date
  periodEnd: Date
  onTrack: boolean
}) {
  const now = new Date()
  const start = periodStart.getTime()
  const end = periodEnd.getTime()
  const curr = Math.min(now.getTime(), end)
  const xFraction = end > start ? (curr - start) / (end - start) : 1
  const yMax = Math.max(goal, current, 1)
  const w = 100
  const h = 50
  const y = (v: number) => h - (v / yMax) * h
  const goalY = y(goal)
  const currentY = y(current)
  const xCurr = xFraction * w
  const fillColor = onTrack ? '#10b981' : '#ef4444' // emerald-500, red-500
  const strokeColor = onTrack ? '#059669' : '#dc2626'   // emerald-600, red-600
  return (
    <div className="min-w-0 rounded overflow-hidden" style={{ background: `${fillColor}18` }}>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-14 block" preserveAspectRatio="none">
        {/* Goal line: (0,h) to (w, goalY) */}
        <line x1={0} y1={h} x2={w} y2={goalY} stroke={strokeColor} strokeWidth={0.8} strokeDasharray="2 1.5" opacity={0.9} />
        {/* Current line: (0,h) to (xCurr, currentY) */}
        <line x1={0} y1={h} x2={xCurr} y2={currentY} stroke={strokeColor} strokeWidth={1.2} />
        {/* Fill under current line */}
        <polygon
          points={`0,${h} ${xCurr},${currentY} ${xCurr},${h} 0,${h}`}
          fill={fillColor}
          opacity={0.35}
        />
      </svg>
    </div>
  )
}

const GST_RATE = 0.15 // PWF invoice amounts are stored ex GST (subtotal); incl view multiplies by 1 + GST_RATE

function getMonthToDateRange(): { from: string; to: string } {
  const now = new Date()
  const from = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
  const to = now.toISOString().split('T')[0]
  return { from, to }
}

function formatExecutiveSummaryStartDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim())
  if (!m) return iso
  const [, y, mo, d] = m
  const month = new Date(Number(y), Number(mo) - 1, Number(d)).toLocaleDateString('en-NZ', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
  return month
}

const SUMMARY_TILE_STYLES = [
  { icon: DollarSign, bg: 'bg-emerald-100', iconColor: 'text-emerald-600' },
  { icon: Calendar, bg: 'bg-sky-100', iconColor: 'text-sky-600' },
  { icon: Users, bg: 'bg-violet-100', iconColor: 'text-violet-600' },
  { icon: Target, bg: 'bg-amber-100', iconColor: 'text-amber-600' },
  { icon: Percent, bg: 'bg-rose-100', iconColor: 'text-rose-600' },
  { icon: Smile, bg: 'bg-teal-100', iconColor: 'text-teal-600' },
  { icon: TrendingUp, bg: 'bg-indigo-100', iconColor: 'text-indigo-600' },
] as const

/** Last 5 full weeks revenue → avg weekly → / non-hidden people count → × 52 = annual rev per person (raw, before GST adjustment). */
function computeRevenuePerPersonAnnual(
  weeks: Array<{ total?: number }> | null | undefined,
  teamCount: number | null
): number | null {
  console.log('[Rev/person] Inputs:', { weeksLength: weeks?.length ?? 0, teamCount })
  if (!weeks?.length || teamCount == null || teamCount <= 0) {
    console.log('[Rev/person] Skipping: no weeks or teamCount invalid')
    return null
  }
  const totalRevenue = weeks.reduce((s, w) => s + (w.total ?? 0), 0)
  const avgWeeklyRevenue = totalRevenue / weeks.length
  const revPerPersonWeekly = avgWeeklyRevenue / teamCount
  const revPerPersonAnnual = revPerPersonWeekly * 52
  console.log('[Rev/person] Steps:', {
    totalRevenue5Weeks: totalRevenue,
    avgWeeklyRevenue,
    teamCount,
    revPerPersonWeekly,
    revPerPersonAnnual,
  })
  return revPerPersonAnnual
}

function progressBarColor(pct: number): string {
  if (pct >= 100) return 'bg-emerald-500'
  if (pct >= 85) return 'bg-emerald-400'
  if (pct >= 70) return 'bg-lime-400'
  if (pct >= 55) return 'bg-amber-400'
  if (pct >= 40) return 'bg-amber-500'
  return 'bg-red-400'
}

type ExecutiveSummaryTile = {
  key: string
  label: string
  value: string
  goalDisplay: string
  goalKey?: keyof ExecutiveSummaryGoals
  goalFormat?: 'currency' | 'percent' | 'number'
  goalEditValue?: number
  /** When true, goal edit/save uses ex-GST stored values (no GST toggle adjustment). */
  goalValueIsStoredBase?: boolean
  completionPct: number
  onTrack: boolean
  styleIndex: number
  chart?: 'ytd' | 'mtd' | 'progress'
  comingSoon?: boolean
  placeholder?: boolean
  subLines?: { label: string; value: string }[]
}

function ExecutiveSummaryTileRow({
  tiles,
  ytdDisplay,
  mtdDisplay,
  goalYtd,
  goalMtd,
  goalsEditable,
  onGoalChange,
  revMultiplier,
  goalSaving,
}: {
  tiles: ExecutiveSummaryTile[]
  ytdDisplay: number | null
  mtdDisplay: number | null
  goalYtd: number
  goalMtd: number
  goalsEditable: boolean
  onGoalChange: (key: keyof ExecutiveSummaryGoals, storedValue: number) => void | Promise<void>
  revMultiplier: number
  goalSaving?: boolean
}) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-6">
      {tiles.map((tile) => {
        if (tile.placeholder) {
          return (
            <div
              key={tile.key}
              className="min-h-[11rem] rounded-lg border border-dashed border-gray-200 bg-gray-50/40"
              aria-label="Reserved metric slot"
            />
          )
        }

        const style = SUMMARY_TILE_STYLES[tile.styleIndex]
        const Icon = style.icon
        const barPct = Math.min(100, tile.completionPct)

        return (
          <div key={tile.key} className="min-w-0 flex flex-col gap-y-6">
            <div className={`relative flex items-center gap-2 min-h-[2.5rem] ${tile.comingSoon ? 'opacity-60' : ''}`}>
              {tile.comingSoon ? <ComingSoonBadge className="absolute top-0 right-0" /> : null}
              <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${style.bg}`}>
                <Icon className={`w-4 h-4 ${style.iconColor}`} aria-hidden />
              </div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider leading-tight">{tile.label}</p>
            </div>

            <div className={`flex items-center min-h-[2rem] ${tile.comingSoon ? 'opacity-60' : ''}`}>
              <span className="text-xl font-bold text-gray-900 tabular-nums">{tile.value}</span>
            </div>

            <EditableGoalLine
              displayValue={tile.goalDisplay}
              editValue={tile.goalEditValue ?? 0}
              format={tile.goalFormat ?? 'number'}
              editable={goalsEditable && tile.goalKey != null}
              muted={tile.comingSoon}
              saving={goalSaving}
              onSave={(displayAmount) => {
                if (!tile.goalKey) return
                const storedValue =
                  tile.goalFormat === 'currency' && !tile.goalValueIsStoredBase
                    ? displayAmount / revMultiplier
                    : displayAmount
                onGoalChange(tile.goalKey, storedValue)
              }}
            />

            {tile.chart === 'ytd' ? (
              <div className="min-w-0">
                <RevenueMiniChart
                  goal={goalYtd}
                  current={ytdDisplay ?? 0}
                  periodStart={new Date(new Date().getFullYear(), 0, 1)}
                  periodEnd={new Date(new Date().getFullYear(), 11, 31)}
                  onTrack={tile.onTrack}
                />
              </div>
            ) : tile.chart === 'mtd' ? (
              <div className="min-w-0">
                <RevenueMiniChart
                  goal={goalMtd}
                  current={mtdDisplay ?? 0}
                  periodStart={new Date(new Date().getFullYear(), new Date().getMonth(), 1)}
                  periodEnd={new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)}
                  onTrack={tile.onTrack}
                />
              </div>
            ) : (
              <div className={`min-w-0 ${tile.comingSoon ? 'opacity-60' : ''}`}>
                <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${progressBarColor(tile.completionPct)}`}
                    style={{ width: `${barPct}%` }}
                    role="progressbar"
                    aria-valuenow={tile.completionPct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <div className={`flex flex-wrap items-center gap-x-2 gap-y-0.5 ${tile.comingSoon ? 'opacity-60' : ''}`}>
                <span className="text-xs font-medium text-gray-700">
                  {tile.completionPct}% of goal
                  {tile.completionPct > 100 ? (
                    <span className="text-emerald-600 ml-0.5">(exceeded)</span>
                  ) : null}
                </span>
                <OnTrackBadge onTrack={tile.onTrack} />
              </div>

              {tile.subLines && tile.subLines.length > 0 ? (
                <ul className="space-y-0.5">
                  {tile.subLines.map((line) => (
                    <li
                      key={line.label}
                      className="flex items-baseline justify-between gap-2 text-[11px] text-gray-500"
                    >
                      <span>{line.label}:</span>
                      <span className="tabular-nums text-gray-700">{line.value}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function ExecutiveSummaryCard({
  pwfYtdTotal,
  pwfMtdTotal,
  revenuePerPersonAnnual,
  revPerPersonPeopleCount,
  inclGst,
  forecastRefresh,
}: {
  pwfYtdTotal: number | null
  pwfMtdTotal: number | null
  /** Annual revenue per person: (avg of last 5 weeks revenue / non-hidden people count) × 52; null if no data */
  revenuePerPersonAnnual: number | null
  /** Non-hidden people used as rev/person denominator; shown in tile header */
  revPerPersonPeopleCount: number | null
  inclGst: boolean
  /** Increments after Fetch from PWF saves a new profitability snapshot. */
  forecastRefresh: number
}) {
  const { currentUser } = useAuth()
  const canEditGoals = isBvtOrgUser(currentUser?.email)
  const {
    goals,
    updateGoal,
    saving: goalSaving,
    error: goalSaveError,
    localOnlyWarning,
    setError: setGoalSaveError,
    setLocalOnlyWarning,
  } = useExecutiveSummaryGoals()
  const { conversionRatePct } = EXECUTIVE_SUMMARY
  const npsScore = NPS_DATA.currentScore
  const { data: activeWorkData, reload: reloadActiveWork } = useActiveWorkOverview()
  useEffect(() => {
    if (forecastRefresh === 0) return
    void reloadActiveWork()
  }, [forecastRefresh, reloadActiveWork])
  const {
    profitMarginPct,
    projectCount: profitMarginProjectCount,
    loading: profitMarginLoading,
  } = useExecutiveSummaryProfitMargin()
  const revMultiplier = inclGst ? 1 + GST_RATE : 1
  const ytdDisplay = pwfYtdTotal != null ? pwfYtdTotal * revMultiplier : null
  const mtdDisplay = pwfMtdTotal != null ? pwfMtdTotal * revMultiplier : null
  const goalYtd = goals.yearToDateRevenue * revMultiplier
  const goalMtd = goals.monthToDateRevenue * revMultiplier
  const revPerPersonDisplay = revenuePerPersonAnnual != null ? revenuePerPersonAnnual * revMultiplier : null
  const goalRevPerPerson = goals.revenuePerFte * revMultiplier
  const monthForecast = useMemo(() => {
    if (!activeWorkData) return null
    const mtd = getMonthToDateRange()
    // Prefer pwfRevenue MTD (same source as the MTD revenue tile); fall back to profitability snapshot.
    const mtdInvoicedExGst = pwfMtdTotal ?? activeWorkData.mtdInvoiceTotal
    const forecast = computeMonthlyForecast(mtd, activeWorkData.activeTasks, mtdInvoicedExGst)
    const invoiced = Math.round(forecast.monthToDateInvoices * 100) / 100
    const goalExGst = goals.monthToDateRevenue
    return {
      total: invoiced + forecast.tasksToComplete,
      goal: goalExGst,
      invoiced,
      toComplete: forecast.tasksToComplete,
      rfi: forecast.rfiTasksValue,
    }
  }, [activeWorkData, pwfMtdTotal, goals.monthToDateRevenue])

  const topRowTiles: ExecutiveSummaryTile[] = [
    {
      key: 'ytd',
      label: 'Year to date revenue',
      value: ytdDisplay != null ? formatCurrency(ytdDisplay) : '—',
      goalDisplay: formatCurrency(goalYtd),
      goalKey: 'yearToDateRevenue',
      goalFormat: 'currency',
      goalEditValue: goalYtd,
      completionPct: ytdDisplay != null ? goalCompletionPct(ytdDisplay, goalYtd) : 0,
      onTrack: ytdDisplay != null && ytdDisplay >= goalYtd,
      styleIndex: 0,
      chart: 'ytd',
    },
    {
      key: 'mtd',
      label: 'Month to date revenue',
      value: mtdDisplay != null ? formatCurrency(mtdDisplay) : '—',
      goalDisplay: formatCurrency(goalMtd),
      goalKey: 'monthToDateRevenue',
      goalFormat: 'currency',
      goalEditValue: goalMtd,
      completionPct: mtdDisplay != null ? goalCompletionPct(mtdDisplay, goalMtd) : 0,
      onTrack: mtdDisplay != null && mtdDisplay >= goalMtd,
      styleIndex: 1,
      chart: 'mtd',
    },
    {
      key: 'rev-per-person',
      label:
        revPerPersonPeopleCount != null
          ? `Revenue per person (${revPerPersonPeopleCount})`
          : 'Revenue per person',
      value: revPerPersonDisplay != null ? formatCurrency(revPerPersonDisplay) : '—',
      goalDisplay: formatCurrency(goalRevPerPerson),
      goalKey: 'revenuePerFte',
      goalFormat: 'currency',
      goalEditValue: goalRevPerPerson,
      completionPct: revPerPersonDisplay != null ? goalCompletionPct(revPerPersonDisplay, goalRevPerPerson) : 0,
      onTrack: revPerPersonDisplay != null && revPerPersonDisplay >= goalRevPerPerson,
      styleIndex: 2,
      chart: 'progress',
    },
    {
      key: 'month-forecast',
      label: 'Month forecast',
      value: monthForecast != null ? formatCurrency(monthForecast.total) : '—',
      goalDisplay: formatCurrency(monthForecast?.goal ?? goals.monthToDateRevenue),
      goalKey: 'monthToDateRevenue',
      goalFormat: 'currency',
      goalEditValue: monthForecast?.goal ?? goals.monthToDateRevenue,
      goalValueIsStoredBase: true,
      completionPct: monthForecast != null ? goalCompletionPct(monthForecast.total, monthForecast.goal) : 0,
      onTrack: monthForecast != null && monthForecast.total >= monthForecast.goal,
      styleIndex: 6,
      chart: 'progress',
      subLines: monthForecast
        ? [
            { label: 'Invoiced (ex GST)', value: formatCurrency(monthForecast.invoiced) },
            { label: 'To complete', value: formatCurrency(monthForecast.toComplete) },
            { label: 'RFI', value: formatCurrency(monthForecast.rfi) },
          ]
        : undefined,
    },
  ]

  const bottomRowTiles: ExecutiveSummaryTile[] = [
    {
      key: 'profit-margin',
      label: 'Profit margin',
      value: profitMarginLoading ? '…' : profitMarginPct != null ? `${profitMarginPct}%` : '—',
      goalDisplay: `${goals.profitMarginPct}%`,
      goalKey: 'profitMarginPct',
      goalFormat: 'percent',
      goalEditValue: goals.profitMarginPct,
      completionPct:
        profitMarginPct != null ? goalCompletionPct(profitMarginPct, goals.profitMarginPct) : 0,
      onTrack: profitMarginPct != null && profitMarginPct >= goals.profitMarginPct,
      styleIndex: 4,
      chart: 'progress',
      subLines:
        profitMarginPct != null && profitMarginProjectCount > 0
          ? [
              {
                label: 'Projects',
                value: String(profitMarginProjectCount),
              },
              {
                label: 'Started',
                value: `≥ ${formatExecutiveSummaryStartDate(EXECUTIVE_SUMMARY_PROFIT_MARGIN_START_DATE)}`,
              },
            ]
          : undefined,
    },
    {
      key: 'conversion',
      label: 'Conversion rate (all projects)',
      value: `${conversionRatePct}%`,
      goalDisplay: `${goals.conversionRatePct}%`,
      goalKey: 'conversionRatePct',
      goalFormat: 'percent',
      goalEditValue: goals.conversionRatePct,
      completionPct: goalCompletionPct(conversionRatePct, goals.conversionRatePct),
      onTrack: conversionRatePct >= goals.conversionRatePct,
      styleIndex: 3,
      chart: 'progress',
      comingSoon: true,
    },
    {
      key: 'nps',
      label: 'NPS score',
      value: String(npsScore),
      goalDisplay: String(goals.npsScore),
      goalKey: 'npsScore',
      goalFormat: 'number',
      goalEditValue: goals.npsScore,
      completionPct: goalCompletionPct(npsScore, goals.npsScore),
      onTrack: npsScore >= goals.npsScore,
      styleIndex: 5,
      chart: 'progress',
      comingSoon: true,
    },
    {
      key: 'reserved',
      label: '',
      value: '',
      goalDisplay: '',
      completionPct: 0,
      onTrack: false,
      styleIndex: 0,
      placeholder: true,
    },
  ]

  const rowProps = {
    ytdDisplay,
    mtdDisplay,
    goalYtd,
    goalMtd,
    goalsEditable: canEditGoals,
    onGoalChange: updateGoal,
    revMultiplier,
    goalSaving,
  }

  return (
    <DashboardCard title="Executive Summary" accent="mint">
      {goalSaveError ? (
        <p className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" role="alert">
          Could not save goal: {goalSaveError}
          <button
            type="button"
            className="ml-2 font-medium underline hover:no-underline"
            onClick={() => setGoalSaveError(null)}
          >
            Dismiss
          </button>
        </p>
      ) : null}
      {localOnlyWarning ? (
        <p className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" role="status">
          Goal saved on this browser only. Deploy Firestore rules to share goals with the team:{' '}
          <code className="text-xs">firebase deploy --only firestore:rules</code>
          <button
            type="button"
            className="ml-2 font-medium underline hover:no-underline"
            onClick={() => setLocalOnlyWarning(false)}
          >
            Dismiss
          </button>
        </p>
      ) : null}
      <div className="space-y-8">
        <ExecutiveSummaryTileRow tiles={topRowTiles} {...rowProps} />
        <ExecutiveSummaryTileRow tiles={bottomRowTiles} {...rowProps} />
      </div>
    </DashboardCard>
  )
}

function ProjectMarginBreakdownTable({ rows }: { rows: ProjectMarginRow[] }) {
  return (
    <div className="mt-4 rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
              <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Budget (alloc.)</th>
              <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Time spent</th>
              <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Invoiced (ex GST)</th>
              <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Margin</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rows.map((p) => (
              <tr key={p.id} className="hover:bg-gray-50/50">
                <td className="px-4 py-2.5 text-gray-900 font-medium">{p.name}</td>
                <td className="px-4 py-2.5 text-gray-600">{p.client}</td>
                <td className="px-4 py-2.5 text-right text-gray-900 tabular-nums">{formatCurrency(p.budgetAlloc)}</td>
                <td className="px-4 py-2.5 text-right text-gray-700 tabular-nums">{p.timeSpentHours} hrs</td>
                <td className="px-4 py-2.5 text-right text-gray-900 tabular-nums">{formatCurrency(p.invoicedTotal)}</td>
                <td className="px-4 py-2.5 text-right text-gray-900 tabular-nums">
                  {p.marginPct != null ? `${p.marginPct}%` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function MarginEfficiencyCard() {
  const [transactionalProjectsOpen, setTransactionalProjectsOpen] = useState(false)
  const [largeProjectsOpen, setLargeProjectsOpen] = useState(false)
  const {
    transactionalMarginPct,
    transactionalProjectCount,
    largeProjectMarginPct,
    largeProjectCount,
    marginByClientType,
    largeProjectRows,
    transactionalProjectRows,
    startDateFrom,
    loading,
  } = useMarginEfficiencyMargins()

  const marginPeriodLabel = `Started ≥ ${formatExecutiveSummaryStartDate(startDateFrom)}`

  return (
    <DashboardCard title="Margin & Efficiency" accent="lavender">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Margin per transactional project</h4>
          <p className="text-2xl font-bold text-gray-900">
            {loading ? '…' : transactionalMarginPct != null ? `${transactionalMarginPct}%` : '—'}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {loading
              ? 'Loading PWF snapshot…'
              : transactionalProjectCount > 0
                ? `${transactionalProjectCount} projects · ${marginPeriodLabel}`
                : `No invoiced transactional projects · ${marginPeriodLabel}`}
          </p>
          {transactionalProjectRows.length > 0 ? (
            <button
              type="button"
              onClick={() => setTransactionalProjectsOpen((o) => !o)}
              className="mt-2 flex items-center gap-1.5 text-sm font-medium text-dashboard-lavender hover:text-violet-700"
            >
              {transactionalProjectsOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              {transactionalProjectsOpen ? 'Hide' : 'Show'} transactional projects ({transactionalProjectRows.length})
            </button>
          ) : null}
        </div>
        <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Margin by client type</h4>
          <p className="text-[10px] text-gray-400 mb-2">Type inferred from client name</p>
          <ul className="space-y-1.5">
            {marginByClientType.map((row) => {
              const style = CLIENT_TYPE_STYLE[row.type]
              const Icon = style?.icon
              return (
                <li key={row.type} className="flex justify-between items-center gap-2 text-sm">
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${style.bg} ${style.text}`}>
                    {Icon && <Icon className="w-3.5 h-3.5" />}
                    {row.type}
                  </span>
                  <span
                    className="font-medium text-gray-900 shrink-0 tabular-nums"
                    title={
                      row.projectCount > 0
                        ? `${row.projectCount} projects · ${formatCurrency(row.revenue)} invoiced (ex GST)`
                        : 'No matching projects in period'
                    }
                  >
                    {loading ? '…' : row.marginPct != null ? `${row.marginPct}%` : '—'}
                  </span>
                </li>
              )
            })}
          </ul>
        </div>
        <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4 md:col-span-1">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Margin by large project</h4>
          <p className="text-2xl font-bold text-gray-900">
            {loading ? '…' : largeProjectMarginPct != null ? `${largeProjectMarginPct}%` : '—'}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {loading
              ? 'Loading PWF snapshot…'
              : largeProjectCount > 0
                ? `${largeProjectCount} projects · ${marginPeriodLabel}`
                : `No invoiced large projects · ${marginPeriodLabel}`}
          </p>
          {largeProjectRows.length > 0 ? (
            <button
              type="button"
              onClick={() => setLargeProjectsOpen((o) => !o)}
              className="mt-2 flex items-center gap-1.5 text-sm font-medium text-dashboard-lavender hover:text-violet-700"
            >
              {largeProjectsOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              {largeProjectsOpen ? 'Hide' : 'Show'} large projects ({largeProjectRows.length})
            </button>
          ) : null}
        </div>
      </div>
      {transactionalProjectsOpen && transactionalProjectRows.length > 0 ? (
        <ProjectMarginBreakdownTable rows={transactionalProjectRows} />
      ) : null}
      {largeProjectsOpen && largeProjectRows.length > 0 ? (
        <ProjectMarginBreakdownTable rows={largeProjectRows} />
      ) : null}
    </DashboardCard>
  )
}

const PWF_REVENUE_BY_WEEK_DOC = 'pwfRevenueByWeek'
const PWF_REVENUE_BY_WEEK_LATEST = 'latest'

export default function Dashboard() {
  const { isAdmin } = useAuth()
  const [pwfUpdatedDate, setPwfUpdatedDate] = useState<string | null>(null)
  /** Latest PWF snapshot from Firestore: pwfRevenue.{latest_date} (ytdTotal, etc.) */
  const [pwfLatestSnapshot, setPwfLatestSnapshot] = useState<{ recordedAt: string; ytdTotal: number; mtdTotal: number } | null>(null)
  /** Last 5 full weeks of revenue from pwfRevenueByWeek/latest (for rev/person). */
  const [pwfRevenueByWeek, setPwfRevenueByWeek] = useState<Array<{ weekLabel?: string; from?: string; to?: string; total: number }> | null>(null)
  /** Non-hidden directory member count (rev/person denominator). */
  const [teamMemberCount, setTeamMemberCount] = useState<number | null>(null)
  const [pwfFetching, setPwfFetching] = useState(false)
  const [pwfFetchError, setPwfFetchError] = useState<string | null>(null)
  const [forecastRefresh, setForecastRefresh] = useState(0)
  /** PWF invoice amounts are stored ex GST; true = show amounts incl GST */
  const [inclGst, setInclGst] = useState(false)

  const revenuePerPersonAnnual = computeRevenuePerPersonAnnual(pwfRevenueByWeek ?? undefined, teamMemberCount)

  const fetchLatestPwfDate = async () => {
    try {
      const snap = await getDocs(
        query(
          collection(db, 'pwfRevenue'),
          orderBy('recordedAt', 'desc'),
          limit(1)
        )
      )
      if (!snap.empty) {
        const doc = snap.docs[0]
        const data = doc.data()
        const latestDate = data.recordedAt ?? doc.id
        setPwfUpdatedDate(latestDate)
        setPwfLatestSnapshot({
          recordedAt: latestDate,
          ytdTotal: typeof data.ytdTotal === 'number' ? data.ytdTotal : 0,
          mtdTotal: typeof data.mtdTotal === 'number' ? data.mtdTotal : 0,
        })
      } else {
        setPwfUpdatedDate(null)
        setPwfLatestSnapshot(null)
      }
    } catch {
      setPwfUpdatedDate(null)
      setPwfLatestSnapshot(null)
    }
  }

  const fetchRevenueByWeekAndTeamCount = async () => {
    try {
      const orgChart = OrgChartService.getInstance()
      console.log('[Rev/person] Fetching pwfRevenueByWeek/latest and non-hidden people count...')
      const [weekSnap, nonHiddenCount] = await Promise.all([
        getDoc(doc(db, PWF_REVENUE_BY_WEEK_DOC, PWF_REVENUE_BY_WEEK_LATEST)),
        orgChart.getNonHiddenTeamMemberCount(),
      ])
      const weekData = weekSnap.exists() ? weekSnap.data() : null
      const weeks = Array.isArray(weekData?.weeks) ? weekData.weeks : null
      console.log('[Rev/person] Fetched:', {
        docExists: weekSnap.exists(),
        weekDataKeys: weekData ? Object.keys(weekData) : null,
        weeksLength: weeks?.length ?? 0,
        weeks: weeks?.map((w) => ({ ...w })) ?? null,
        nonHiddenPeopleCount: nonHiddenCount,
      })
      setPwfRevenueByWeek(weeks)
      setTeamMemberCount(nonHiddenCount)
    } catch (err) {
      console.log('[Rev/person] Fetch error:', err)
      setPwfRevenueByWeek(null)
      setTeamMemberCount(null)
    }
  }

  useEffect(() => {
    fetchLatestPwfDate()
    fetchRevenueByWeekAndTeamCount()
  }, [])

  // Expose one-off PWF 24-month backfill for Chrome console (admin): runPwf24MonthBackfill()
  useEffect(() => {
    if (typeof window === 'undefined' || !isAdmin) return
    const run = async () => {
      const functions = getFunctions()
      const backfill = httpsCallable<unknown, { success: boolean; weeksWritten?: number; invoiceCount?: number; from?: string; to?: string }>(functions, 'backfillPwf24MonthsByWeek')
      const result = await backfill({})
      console.log('backfillPwf24MonthsByWeek result:', result.data)
      return result.data
    }
    ;(window as unknown as { runPwf24MonthBackfill?: () => Promise<unknown> }).runPwf24MonthBackfill = run
    return () => {
      delete (window as unknown as { runPwf24MonthBackfill?: () => Promise<unknown> }).runPwf24MonthBackfill
    }
  }, [isAdmin])

  const handleFetchPwfRevenue = async () => {
    if (!isAdmin || pwfFetching) return
    setPwfFetching(true)
    setPwfFetchError(null)
    try {
      const functions = getFunctions()
      const saveSnapshot = httpsCallable<unknown, { success: boolean; recordedAt?: string }>(functions, 'savePwfRevenueSnapshot')
      const saveRevenueByWeek = httpsCallable<unknown, { success: boolean; recordedAt?: string }>(functions, 'savePwfRevenueByWeekSnapshot')
      const saveTop20Clients = httpsCallable<unknown, { success: boolean; recordedAt?: string }>(functions, 'savePwfTop20ClientsSnapshot')
      await Promise.all([
        saveSnapshot({}),
        saveRevenueByWeek({}),
        saveTop20Clients({}),
        savePwfProfitabilitySnapshotFromPwf(),
      ])
      setForecastRefresh((n) => n + 1)
      await Promise.all([fetchLatestPwfDate(), fetchRevenueByWeekAndTeamCount()])
    } catch (err) {
      console.error('Failed to fetch PWF revenue:', err)
      setPwfFetchError(err instanceof Error ? err.message : 'Failed to fetch from ProWorkflow')
    } finally {
      setPwfFetching(false)
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Business Analytics Dashboard</h1>
          <button
            type="button"
            onClick={() => setInclGst((prev) => !prev)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            title={inclGst ? 'Revenue and goals include 15% GST. Click to show excl GST.' : 'Revenue and goals exclude GST. Click to show incl GST.'}
          >
            {inclGst ? 'Incl GST' : 'Excl GST'}
          </button>
        </div>
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <span>PWF updated: {pwfUpdatedDate ?? '—'}</span>
          {isAdmin && (
            <button
              type="button"
              onClick={handleFetchPwfRevenue}
              disabled={pwfFetching}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${pwfFetching ? 'animate-spin' : ''}`} />
              {pwfFetching ? 'Fetching…' : 'Fetch from PWF'}
            </button>
          )}
        </div>
      </div>
      {pwfFetching ? (
        <p className="mb-4 text-sm text-gray-600">
          Refreshing revenue and task prices. Task prices usually take 1–3 minutes.
        </p>
      ) : null}
      {pwfFetchError ? (
        <p className="mb-4 text-sm text-red-700">{pwfFetchError}</p>
      ) : null}

      <div className="space-y-6">
        <section className="rounded-xl bg-dashboard-mint/10 py-6 px-4 sm:px-6 -mx-4 sm:-mx-6">
          <ExecutiveSummaryCard
            pwfYtdTotal={pwfLatestSnapshot?.ytdTotal ?? null}
            pwfMtdTotal={pwfLatestSnapshot?.mtdTotal ?? null}
            revenuePerPersonAnnual={revenuePerPersonAnnual}
            revPerPersonPeopleCount={teamMemberCount}
            inclGst={inclGst}
            forecastRefresh={forecastRefresh}
          />
        </section>

        <section className="rounded-xl bg-dashboard-lavender/10 py-6 px-4 sm:px-6 -mx-4 sm:-mx-6">
          <MarginEfficiencyCard />
        </section>

        <section className="rounded-xl bg-dashboard-terracotta/10 py-6 px-4 sm:px-6 -mx-4 sm:-mx-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span className="w-1 h-6 rounded-full bg-dashboard-terracotta" aria-hidden />
            Client Relationships
          </h2>
          <Top20Clients />
        </section>

        <section className="rounded-xl bg-dashboard-rose/10 py-6 px-4 sm:px-6 -mx-4 sm:-mx-6 relative">
          <div className="absolute top-6 right-4 sm:right-6 z-10">
            <ComingSoonBadge />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span className="w-1 h-6 rounded-full bg-dashboard-rose" aria-hidden />
            Client Feedback
          </h2>
          <div className="opacity-60 pointer-events-none select-none grid grid-cols-1 lg:grid-cols-2 gap-6">
            <NpsInfographic />
            <SatisfactionChart />
            <div className="lg:col-span-2">
              <FeedbackTable />
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
